import { useCallback, useState } from 'react';

import usePreSignedUrlMultipartUploadCallback from './usePreSignedUrlMultipartUploadCallback';
import { MultipartUploader } from '../utils';

export function useUploadFilesMultipartToS3Callback(
  filesProgress,
  setFileProgress,
  fileFolder,
  onEndLoad = () => {
    return false;
  },
  options = {
    chunkSize: 1024 * 1024 * 20,
    threadsQuantity: 5
  }
) {
  const [loading, setLoading] = useState(false);
  const { callback: preSignedUrlUploadCallback } = usePreSignedUrlMultipartUploadCallback();

  const callback = useCallback(
    async ({ files }) => {
      try {
        setLoading(true);
        const uploadedFiles = await Promise.all(
          files.map(async (file) => {
            const uploader = new MultipartUploader(
              {
                file: file.file,
                company: file.company,
                fileName: file.file.name,
                fileKey: file.fileKey,
                ...options
              }
            );
            const {
              urls,
              bucket,
              key,
              uploadId
            } = await preSignedUrlUploadCallback({
              fileFolder,
              fileKey: file.fileKey,
              company: file.company,
              parts: uploader.getNumberOfParts(),
              headers: {
              }
            });
            // eslint-disable-next-line no-param-reassign
            file.fileKey = `${bucket}/${key}`;
            setFileProgress({
              ...file,
              fileKey: `${bucket}/${key}`
            });

            uploader.withParts(urls).withUploadId(uploadId).withKey(key).withBuket(bucket);
            uploader.onComplete(async ({
              fileId,
              fileKey,
              parts
            }) => {
              await preSignedUrlUploadCallback({
                uploadId: fileId,
                fileKey: fileKey,
                tags: parts
              });
              // eslint-disable-next-line no-param-reassign
              file.meta.progress = 100;
              setFileProgress({
                ...file
              });
            });
            uploader.onProgress(({
              sent = 0,
              total = 0,
              percentage = 0
            }) => {
              if (percentage > 1) {
                console.log('end:: onProgress', file.key, percentage);
                // eslint-disable-next-line no-param-reassign
                file.meta.progress = percentage;
                setFileProgress({
                  ...file,
                  meta: {
                    requestInstance: uploader,
                    sent,
                    total,
                    percentage,
                    progress: percentage
                  }
                });
              }
            });
            await uploader.start();
            return file;
          })
        );
        onEndLoad();
        setLoading(false);
        return uploadedFiles;
      } catch (error) {
        setLoading(false);
        throw error;
      }
    },
    [fileFolder, onEndLoad, options, preSignedUrlUploadCallback, setFileProgress]
  );

  const formatFile = useCallback(
    ({ file, group }) => {
      const date = new Date();
      const type = file.type || 'application/octet-stream';
      const key = `${type}/${date.getTime()}.${file.name}`
        .replaceAll(' ', '_')
        .replaceAll('Ñ', 'N')
        .replaceAll('ñ', 'n')
        .replaceAll(/[^A-Za-z0-9-_.!~*'()]/g, '_');
      return {
        date,
        name: file.name,
        size: file.size,
        type,
        file,
        key,
        group,
        fileKey: key,
        meta: {
          progress: 1
        }
      };
    },
    []
  );

  const removeFile = useCallback(
    (file) => {
      if (
        filesProgress[file.key]
        && filesProgress[file.key].meta
        && filesProgress[file.key].meta.requestInstance
        && filesProgress[file.key].meta.progress < 100
      ) {
        filesProgress[file.key].meta.requestInstance.abort('Upload aborted');
      }
    },
    [filesProgress]
  );
  return {
    formatFile,
    callback,
    loading,
    filesProgress,
    removeFile
  };
}

export default useUploadFilesMultipartToS3Callback;
